/**
 * ETHOS Billing Provider Abstraction
 * Determines correct billing provider per platform.
 * Prevents Android native from routing digital purchases to Stripe web.
 */

import { isNativeApp, isNativeAndroid } from "./platform";

export const PROVIDER_MODE = {
  STRIPE_WEB: "stripe_web",
  GOOGLE_PLAY_FUTURE: "google_play_future",
  APPLE_IAP_FUTURE: "apple_iap_future",
  DISABLED_PREVIEW: "disabled_preview",
};

/**
 * Returns the active billing provider for the current environment.
 * - Web → Stripe (live)
 * - Android native → Google Play (future, not yet implemented)
 * - iOS native → Apple IAP (future, not yet implemented)
 */
export function getActiveProvider() {
  if (isNativeAndroid()) return PROVIDER_MODE.GOOGLE_PLAY_FUTURE;
  if (isNativeApp()) return PROVIDER_MODE.APPLE_IAP_FUTURE;
  return PROVIDER_MODE.STRIPE_WEB;
}

/**
 * Returns true only if this environment can complete a live purchase.
 * Use this to decide whether to show a purchase button or an informational message.
 */
export function canPurchase() {
  const provider = getActiveProvider();
  return provider === PROVIDER_MODE.STRIPE_WEB;
}

/**
 * Returns safe copy for environments where purchasing is not yet available.
 */
export function getUnavailableCopy(context = "subscription") {
  const provider = getActiveProvider();
  if (provider === PROVIDER_MODE.GOOGLE_PLAY_FUTURE) {
    if (context === "subscription") {
      return "Upgrade access is being prepared for the app. You can upgrade at ethosnetworking.com for now.";
    }
    if (context === "offerings") {
      return "Purchases are being prepared for the app. Your existing offerings still work here.";
    }
    return "This purchase is being prepared for the app.";
  }
  if (provider === PROVIDER_MODE.APPLE_IAP_FUTURE) {
    return "ETHOS for iOS is coming soon.";
  }
  return null;
}

/**
 * Returns human-readable subscription provider label.
 * Use to replace "Managed via Google Play" with accurate copy.
 */
export function getProviderLabel(subscriptionProvider) {
  switch (subscriptionProvider) {
    case "stripe_web":
    case "stripe":
      return "Managed through ETHOS web billing.";
    case "google_play":
      return "Managed via Google Play. To cancel, go to Play Store → Subscriptions.";
    case "apple_iap":
      return "Managed via App Store. To cancel, go to Settings → Subscriptions.";
    case "founder":
      return "Founder access active.";
    default:
      return "Subscription active.";
  }
}
